import type {
  OrgUserDetail,
  OrgUserDetailShopRolesItem,
  OrgUserListItem,
  OrgUserRole,
  PermissionSet,
  ShopUserListItem,
  UserRoleRole,
} from '@/shared/api';

// A single shop and the role the user holds there, carried by the org
// member-detail projection.
export type OrgShopRole = OrgUserDetailShopRolesItem;

// Normalized, id-keyed shape the constructor wraps. The canonical
// `UserWithPermissions` widens into it directly; lean projections (org/shop
// user rows, which key identity as `userId` and may omit core fields) are
// normalized into it by the static factories below.
export type UserData = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  avatarUrl?: string | null;
  color?: string | null;
  role?: UserRoleRole;
  permissions?: PermissionSet;
  orgRole?: OrgUserRole;
  shopCount?: number;
  orgShopRoles?: OrgShopRole[];
};

export class User {
  declare id: string;
  declare email: string;
  declare firstName: string;
  declare lastName: string;
  declare phone?: string;
  declare avatarUrl?: string | null;
  declare color?: string | null;
  declare role?: UserRoleRole;
  declare permissions?: PermissionSet;
  declare orgRole?: OrgUserRole;
  declare shopCount?: number;
  declare orgShopRoles?: OrgShopRole[];

  constructor(data: UserData) {
    Object.assign(this, data);
  }

  get displayName(): string {
    const fullName = [this.firstName, this.lastName].filter(Boolean).join(' ');
    return fullName || this.email;
  }

  get initials(): string {
    const firstInitial = this.firstName?.charAt(0) ?? '';
    const lastInitial = this.lastName?.charAt(0) ?? '';
    const initials = `${firstInitial}${lastInitial}`.toUpperCase();
    return initials || this.email.charAt(0).toUpperCase();
  }

  get roleNameKey(): string {
    switch (this.role) {
      case 'admin':
        return 'roles.admin';
      case 'service_advisor':
        return 'roles.serviceAdvisor';
      case 'shop_owner':
        return 'roles.shopOwner';
      case 'manager':
        return 'roles.manager';
      case 'front_desk':
        return 'roles.frontDesk';
      case 'technician':
      default:
        return 'roles.technician';
    }
  }

  get isOrgOwner(): boolean {
    return this.orgRole === 'org_owner';
  }

  get isOrgAdmin(): boolean {
    return this.orgRole === 'org_admin';
  }

  static fromShopUser(row: ShopUserListItem): User {
    return new User({
      id: row.userId,
      email: row.email,
      firstName: row.firstName ?? '',
      lastName: row.lastName ?? '',
      role: row.role,
      orgRole: row.orgRole,
    });
  }

  static fromOrgMember(row: OrgUserListItem): User {
    return new User({
      id: row.userId,
      email: row.email,
      firstName: row.firstName ?? '',
      lastName: row.lastName ?? '',
      orgRole: row.orgRole,
      shopCount: row.shopCount,
    });
  }

  static fromOrgMemberDetail(detail: OrgUserDetail): User {
    return new User({
      id: detail.user.id,
      email: detail.user.email,
      firstName: detail.user.firstName ?? '',
      lastName: detail.user.lastName ?? '',
      orgRole: detail.orgRole,
      orgShopRoles: detail.shopRoles,
    });
  }
}
